/**
 * Comportements de saisie de l'éditeur (spécification §7) et mise en forme par la barre
 * d'outils. Fonctions sur l'état CodeMirror, sans vue : testables telles quelles.
 *
 * - Fermeture automatique : à l'ouverture d'un format (`*`, `**`, `~~`, `` ` ``, `$`, `{{`,
 *   `[`), le symbole de fermeture est inséré après le curseur ; taper soi-même le symbole
 *   de fermeture passe par-dessus.
 * - Entrée sort du format en cours (gras, italique, barré, code, formule, trou, lien) ; dans
 *   une liste ou une citation, nouvel élément, et sortie sur un élément vide ; dans un bloc
 *   de code, nouvelle ligne, et sortie sur une ligne vide finale.
 */
import { insertNewlineContinueMarkup } from '@codemirror/lang-markdown';
import { insertNewlineAndIndent } from '@codemirror/commands';
import { syntaxTree } from '@codemirror/language';
import {
  EditorSelection,
  type EditorState,
  type StateCommand,
  type TransactionSpec,
} from '@codemirror/state';
import type { SyntaxNode } from '@lezer/common';

/** Symboles qui se ferment seuls, avec leur symbole de fermeture. */
const PAIRES: Record<string, string> = { '*': '*', '~': '~', '`': '`', $: '$', '{': '}', '[': ']' };
/** Symboles dont le double ouvre un autre format (`**` gras, `~~` barré, `$$` formule en bloc). */
const DOUBLABLES = new Set(['*', '~', '$']);
const FERMANTS = new Set(['}', ']']);

const estMot = (c: string) => /[\p{L}\p{N}_]/u.test(c);
/** Contexte où un symbole ouvrant peut se fermer seul : avant un blanc, une fin, une ponctuation. */
const avantFermable = (c: string) => c === '' || /[\s)\]}.,;:!?»'"]/.test(c);

function car(state: EditorState, pos: number): string {
  return pos < 0 || pos >= state.doc.length ? '' : state.doc.sliceString(pos, pos + 1);
}

/**
 * Fermeture automatique, pour un caractère tapé (`EditorView.inputHandler`). Renvoie la
 * transaction à appliquer, ou `null` pour laisser la saisie normale.
 */
export function fermetureAuto(
  state: EditorState,
  from: number,
  to: number,
  texte: string,
): TransactionSpec | null {
  if (state.selection.ranges.length > 1) return null;
  const fermant = PAIRES[texte];

  // Symbole fermant tapé devant le même symbole : on passe par-dessus.
  if (FERMANTS.has(texte) && from === to && car(state, from) === texte) {
    return { selection: { anchor: from + 1 }, scrollIntoView: true, userEvent: 'input' };
  }
  if (fermant === undefined) return null;

  // Sélection : on l'entoure du format.
  if (from !== to) {
    return {
      changes: [
        { from, insert: texte },
        { from: to, insert: fermant },
      ],
      selection: EditorSelection.range(from + 1, to + 1),
      userEvent: 'input',
    };
  }

  const avant = car(state, from - 1);
  const apres = car(state, from);
  const symetrique = fermant === texte;

  if (symetrique && apres === texte) {
    // `*|*` → `**|**` : le format double (gras, barré, formule en bloc).
    const avantAvant = car(state, from - 2);
    const paireVide =
      avant === texte &&
      !estMot(avantAvant) &&
      avantAvant !== texte &&
      car(state, from + 1) !== texte;
    if (DOUBLABLES.has(texte) && paireVide) {
      return {
        changes: { from, insert: texte + texte },
        selection: { anchor: from + 1 },
        userEvent: 'input',
      };
    }
    // Sinon : symbole de fermeture tapé à la main, on passe par-dessus.
    return { selection: { anchor: from + 1 }, scrollIntoView: true, userEvent: 'input' };
  }

  if (!avantFermable(apres)) return null;
  // `*` seul en début de ligne (puce possible) puis un second `*` : c'est un gras, `**|**`.
  if (texte === '*') {
    const ligne = state.doc.lineAt(from);
    if (/^\s*\*$/.test(state.doc.sliceString(ligne.from, from))) {
      return {
        changes: { from, insert: '***' },
        selection: { anchor: from + 1 },
        userEvent: 'input',
      };
    }
  }
  if (symetrique) {
    // Pas de fermeture au milieu d'un mot (« 2*3 », « 5$ ») ni pour une puce de liste.
    if (estMot(avant)) return null;
    const ligne = state.doc.lineAt(from);
    const debutDeLigne = state.doc.sliceString(ligne.from, from);
    if (texte === '*' && /^\s*$/.test(debutDeLigne)) return null;
    if (texte === '`' && /^\s*`*$/.test(debutDeLigne) && debutDeLigne.includes('`')) return null;
  }
  return {
    changes: { from, insert: texte + fermant },
    selection: { anchor: from + 1 },
    userEvent: 'input',
  };
}

/** Retour arrière dans une paire vide (`*|*`, `**|**`, `{{|}}`, `[|]`) : la paire disparaît. */
export const supprimerPaireVide: StateCommand = ({ state, dispatch }) => {
  const plage = state.selection.main;
  if (!plage.empty || state.selection.ranges.length > 1) return false;
  const p = plage.head;
  const ouvrant = car(state, p - 1);
  const fermant = PAIRES[ouvrant];
  if (fermant === undefined || car(state, p) !== fermant) return false;
  let n = 1;
  while (n < 2 && car(state, p - 1 - n) === ouvrant && car(state, p + n) === fermant) n++;
  if (fermant === ouvrant && estMot(car(state, p - 1 - n))) return false;
  dispatch(state.update({ changes: { from: p - n, to: p + n }, userEvent: 'delete.backward' }));
  return true;
};

/** Formats en ligne dont Entrée fait sortir. */
const FORMATS_EN_LIGNE = new Set([
  'Emphasis',
  'StrongEmphasis',
  'Strikethrough',
  'InlineCode',
  'Formule',
  'Trou',
  'Link',
  'Surlignage',
]);

function noeudEnglobant(
  state: EditorState,
  pos: number,
  noms: ReadonlySet<string>,
): SyntaxNode | null {
  for (let n: SyntaxNode | null = syntaxTree(state).resolveInner(pos, 1); n; n = n.parent) {
    if (noms.has(n.name) && n.from < pos && pos < n.to) return n;
  }
  for (let n: SyntaxNode | null = syntaxTree(state).resolveInner(pos, -1); n; n = n.parent) {
    if (noms.has(n.name) && n.from < pos && pos < n.to) return n;
  }
  return null;
}

/** Entrée : sortir du format, du bloc de code, continuer une liste, ou aller à la ligne. */
export const entree: StateCommand = (cible) => {
  const { state, dispatch } = cible;
  if (state.selection.ranges.length > 1 || !state.selection.main.empty)
    return insertNewlineContinueMarkup(cible);
  const p = state.selection.main.head;

  // Dans un format en ligne : on passe après sa fermeture.
  const format = noeudEnglobant(state, p, FORMATS_EN_LIGNE);
  if (format !== null) {
    dispatch(
      state.update({ selection: { anchor: format.to }, scrollIntoView: true, userEvent: 'select' }),
    );
    return true;
  }
  // Paire vide pas encore reconnue comme un format (`**|**`, `{{|}}`) : on passe après.
  const ouvrant = car(state, p - 1);
  if (PAIRES[ouvrant] !== undefined && car(state, p) === PAIRES[ouvrant]) {
    let fin = p;
    while (car(state, fin) === PAIRES[ouvrant]) fin++;
    dispatch(state.update({ selection: { anchor: fin }, userEvent: 'select' }));
    return true;
  }

  // Bloc de code.
  const ligne = state.doc.lineAt(p);
  const code = noeudEnglobant(state, p, new Set(['FencedCode'])) ?? noeudBlocCodeOuvert(state, p);
  if (code !== null) {
    const ouverture = state.doc.lineAt(code.from);
    const marques = code.node.getChildren('CodeMark');
    const fermee =
      marques.length >= 2 && state.doc.lineAt(marques.at(-1)!.from).number > ouverture.number;
    // Sur la ligne d'ouverture d'un bloc non fermé : on ajoute la clôture.
    if (ligne.number === ouverture.number && !fermee && p === ligne.to) {
      const clotureIndent = /^\s*/.exec(ligne.text)![0];
      dispatch(
        state.update({
          changes: { from: p, insert: `\n${clotureIndent}\n${clotureIndent}\`\`\`` },
          selection: { anchor: p + 1 + clotureIndent.length },
          userEvent: 'input',
        }),
      );
      return true;
    }
    // Sur une ligne vide finale : on sort du bloc.
    if (ligne.number > ouverture.number && ligne.text.trim() === '') {
      const fin = state.doc.lineAt(code.to);
      const derniere = fermee ? fin.number - 1 : fin.number;
      if (ligne.number === derniere) {
        if (fermee) {
          dispatch(
            state.update({
              changes: [
                { from: ligne.from - 1, to: ligne.to },
                { from: fin.to, insert: '\n' },
              ],
              selection: { anchor: fin.to - (ligne.to - ligne.from + 1) + 1 },
              userEvent: 'input',
            }),
          );
        } else {
          dispatch(
            state.update({
              changes: { from: ligne.from, to: ligne.to, insert: '```\n' },
              selection: { anchor: ligne.from + 4 },
              userEvent: 'input',
            }),
          );
        }
        return true;
      }
    }
    return insertNewlineAndIndent(cible);
  }
  // Élément de liste ou ligne de citation vide : on sort de la liste ou de la citation.
  const marque = /^(\s*)([-*+]|\d+[.)]|>)\s*$/.exec(ligne.text);
  if (marque && p === ligne.to) {
    dispatch(state.update({ changes: { from: ligne.from, to: ligne.to }, userEvent: 'delete' }));
    return true;
  }
  return insertNewlineContinueMarkup(cible) || insertNewlineAndIndent(cible);
};

/** Bloc de code commencé sur une ligne au-dessus et pas encore fermé (fin du texte). */
function noeudBlocCodeOuvert(state: EditorState, pos: number): SyntaxNode | null {
  const noeud = syntaxTree(state).resolveInner(pos, -1);
  for (let n: SyntaxNode | null = noeud; n; n = n.parent) if (n.name === 'FencedCode') return n;
  return null;
}

// ---------------------------------------------------------------------------------------
// Barre d'outils
// ---------------------------------------------------------------------------------------

export type FormatDEditeur =
  | 'titre-1'
  | 'titre-2'
  | 'titre-3'
  | 'gras'
  | 'italique'
  | 'barre'
  | 'surlignage'
  | 'liste'
  | 'liste-numerotee'
  | 'citation'
  | 'lien'
  | 'code'
  | 'bloc-de-code'
  | 'formule'
  | 'image'
  | 'trou';

const FORMAT_DU_NOEUD: Record<string, FormatDEditeur> = {
  StrongEmphasis: 'gras',
  Emphasis: 'italique',
  Strikethrough: 'barre',
  Surlignage: 'surlignage',
  InlineCode: 'code',
  Formule: 'formule',
  FormuleBloc: 'formule',
  Trou: 'trou',
  Link: 'lien',
  ATXHeading1: 'titre-1',
  ATXHeading2: 'titre-2',
  ATXHeading3: 'titre-3',
  BulletList: 'liste',
  OrderedList: 'liste-numerotee',
  Blockquote: 'citation',
  FencedCode: 'bloc-de-code',
};

/** Formats présents au curseur (boutons enfoncés de la barre d'outils). */
export function formatsActifs(state: EditorState): FormatDEditeur[] {
  const p = state.selection.main.head;
  const actifs = new Set<FormatDEditeur>();
  for (const cote of [-1, 1] as const) {
    for (let n: SyntaxNode | null = syntaxTree(state).resolveInner(p, cote); n; n = n.parent) {
      const format = FORMAT_DU_NOEUD[n.name];
      if (format === undefined) continue;
      const enLigne = FORMATS_EN_LIGNE.has(n.name) || n.name === 'FormuleBloc';
      if (
        !enLigne ||
        (n.from < p && p < n.to) ||
        (n.from === p && cote === 1) ||
        (n.to === p && cote === -1)
      ) {
        actifs.add(format);
      }
    }
  }
  return [...actifs];
}

const MARQUES_EN_LIGNE: Partial<Record<FormatDEditeur, [string, string]>> = {
  gras: ['**', '**'],
  italique: ['*', '*'],
  barre: ['~~', '~~'],
  code: ['`', '`'],
  formule: ['$', '$'],
  trou: ['{{', '}}'],
};

const NOEUD_DU_FORMAT: Partial<Record<FormatDEditeur, string[]>> = {
  gras: ['StrongEmphasis'],
  italique: ['Emphasis'],
  barre: ['Strikethrough'],
  code: ['InlineCode'],
  formule: ['Formule', 'FormuleBloc'],
  trou: ['Trou'],
  surlignage: ['Surlignage'],
  lien: ['Link'],
};

const MARQUES_DE_NOEUD = new Set([
  'EmphasisMark',
  'StrikethroughMark',
  'CodeMark',
  'FormuleMarque',
  'TrouMarque',
  'SurlignageMarque',
]);

/** Retire les marques d'un format (bouton enfoncé : second clic). */
function retirerFormat(state: EditorState, noeud: SyntaxNode): TransactionSpec {
  const marques: { from: number; to: number }[] = [];
  for (let c = noeud.firstChild; c; c = c.nextSibling) {
    // Seules les marques directes du nœud (pas celles d'un format imbriqué).
    if (
      MARQUES_DE_NOEUD.has(c.name) ||
      (noeud.name === 'Link' && c.name !== 'URL' && c.name === 'LinkMark')
    ) {
      marques.push({ from: c.from, to: c.to });
    }
  }
  if (noeud.name === 'Link') {
    const crochets = noeud.getChildren('LinkMark');
    if (crochets.length >= 2) {
      return {
        changes: [
          { from: crochets[0]!.from, to: crochets[0]!.to },
          { from: crochets[1]!.from, to: noeud.to },
        ],
      };
    }
  }
  return { changes: marques };
}

export interface OptionsFormat {
  /** Couleur de surlignage : base (`jaune` par défaut) ou couleur #rrggbb. */
  couleur?: string;
  /** Chemin d'une image déjà envoyée (format `image`). */
  image?: { chemin: string; texteAlternatif?: string };
}

/** Applique un format de la barre d'outils au champ (sélection ou curseur). */
export function appliquerFormat(
  state: EditorState,
  format: FormatDEditeur,
  options: OptionsFormat = {},
): TransactionSpec {
  const plage = state.selection.main;
  const actif = (NOEUD_DU_FORMAT[format] ?? [])
    .map((nom) => noeudEnglobantOuBord(state, plage.from, nom))
    .find((n) => n !== null);

  // Surlignage : changer de couleur sur un surlignage existant, ou le retirer (`null`).
  if (format === 'surlignage') {
    const ouvrante =
      options.couleur === undefined || options.couleur === 'jaune'
        ? '=='
        : `=={${options.couleur}}`;
    if (actif) {
      const premiere = actif.firstChild!;
      if (options.couleur === 'aucune') return retirerFormat(state, actif);
      return { changes: { from: premiere.from, to: premiere.to, insert: ouvrante } };
    }
    if (options.couleur === 'aucune') return {};
    return entourer(state, ouvrante, '==');
  }

  const marques = MARQUES_EN_LIGNE[format];
  if (marques) {
    if (actif) return retirerFormat(state, actif);
    return entourer(state, marques[0], marques[1]);
  }

  switch (format) {
    case 'titre-1':
    case 'titre-2':
    case 'titre-3': {
      const niveau = Number(format.slice(-1));
      return prefixerLignes(state, (texte) => {
        const actuel = /^(#{1,6}) /.exec(texte);
        if (actuel && actuel[1]!.length === niveau) return texte.slice(niveau + 1);
        return `${'#'.repeat(niveau)} ${actuel ? texte.slice(actuel[0].length) : texte}`;
      });
    }
    case 'liste':
      return prefixerLignes(state, (texte) =>
        /^- /.test(texte) ? texte.slice(2) : `- ${texte.replace(/^\d+\. /, '')}`,
      );
    case 'liste-numerotee': {
      let n = 0;
      return prefixerLignes(state, (texte) => {
        if (/^\d+\. /.test(texte)) return texte.replace(/^\d+\. /, '');
        n += 1;
        return `${n}. ${texte.replace(/^- /, '')}`;
      });
    }
    case 'citation':
      return prefixerLignes(state, (texte) =>
        /^> ?/.test(texte) ? texte.replace(/^> ?/, '') : `> ${texte}`,
      );
    case 'lien': {
      if (actif) return retirerFormat(state, actif);
      if (plage.empty) {
        return {
          changes: { from: plage.from, insert: '[]()' },
          selection: { anchor: plage.from + 1 },
        };
      }
      return {
        changes: [
          { from: plage.from, insert: '[' },
          { from: plage.to, insert: ']()' },
        ],
        selection: { anchor: plage.to + 3 },
      };
    }
    case 'bloc-de-code': {
      const ligne = state.doc.lineAt(plage.from);
      const avant = ligne.text.trim() === '' ? '' : '\n';
      const contenu = state.sliceDoc(plage.from, plage.to);
      const insertion = `${avant}\`\`\`\n${contenu}\n\`\`\`\n`;
      const debut = plage.from + avant.length + 4;
      return {
        changes: { from: plage.from, to: plage.to, insert: insertion },
        selection: EditorSelection.range(debut, debut + contenu.length),
      };
    }
    case 'image': {
      if (!options.image) return {};
      const texte = `![${options.image.texteAlternatif ?? ''}](${options.image.chemin})`;
      return {
        changes: { from: plage.from, to: plage.to, insert: texte },
        selection: { anchor: plage.from + texte.length },
      };
    }
    default:
      return {};
  }
}

function noeudEnglobantOuBord(state: EditorState, pos: number, nom: string): SyntaxNode | null {
  for (const cote of [-1, 1] as const) {
    for (let n: SyntaxNode | null = syntaxTree(state).resolveInner(pos, cote); n; n = n.parent) {
      if (n.name === nom && n.from <= pos && pos <= n.to) return n;
    }
  }
  return null;
}

function entourer(state: EditorState, ouvrante: string, fermante: string): TransactionSpec {
  const { from, to } = state.selection.main;
  return {
    changes: [
      { from, insert: ouvrante },
      { from: to, insert: fermante },
    ],
    selection: EditorSelection.range(from + ouvrante.length, to + ouvrante.length),
  };
}

function prefixerLignes(
  state: EditorState,
  transformer: (texte: string) => string,
): TransactionSpec {
  const { from, to } = state.selection.main;
  const premiere = state.doc.lineAt(from).number;
  const derniere = state.doc.lineAt(to).number;
  const changes = [];
  for (let i = premiere; i <= derniere; i++) {
    const ligne = state.doc.line(i);
    const texte = transformer(ligne.text);
    if (texte !== ligne.text) changes.push({ from: ligne.from, to: ligne.to, insert: texte });
  }
  const transaction: TransactionSpec = { changes };
  if (premiere === derniere && changes.length === 1) {
    const nouvelle = changes[0]!.insert;
    transaction.selection = { anchor: state.doc.line(premiere).from + nouvelle.length };
  }
  return transaction;
}
